import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';
import { addLines, hasCartDrawer } from '@theme/vara-cart';

/**
 * VIP membership.
 *
 * Membership is one fact: the $0 membership product is in the cart. There is never more than one
 * membership line, and it never stays behind in a cart with no products. Member prices shown by
 * the theme are display only; Shopify checkout is blocked for member carts in
 * snippets/cart-summary.liquid and the external checkout prices the order itself.
 */

/** @returns {Promise<any>} The current cart from the AJAX API. */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
  return response.json();
}

/**
 * @param {any} cart
 * @param {string} markerVariantId
 * @returns {number} Quantity of the membership line, 0 when absent.
 */
function markerQuantity(cart, markerVariantId) {
  let quantity = 0;
  for (const item of cart.items ?? []) {
    if (String(item.variant_id) === markerVariantId) quantity += item.quantity;
  }
  return quantity;
}

/**
 * Sets the membership line to a quantity (0 removes it).
 * @param {string} markerVariantId
 * @param {number} quantity
 * @param {string[]} [sections] - Section ids to render in the response.
 */
async function setMarkerQuantity(markerVariantId, quantity, sections = []) {
  const body = JSON.stringify({
    id: markerVariantId,
    quantity,
    ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
  });
  const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
  const json = await response.json();
  if (!response.ok || json.status) throw new Error(json.description || json.message || 'Cart change failed');
  return json;
}

/**
 * Adds one membership line to the cart.
 * @param {string} markerVariantId
 * @param {string[]} [sections] - Section ids to render in the response.
 * @returns {Promise<Record<string, string> | undefined>} Rendered sections, when requested.
 */
async function addMarker(markerVariantId, sections = []) {
  const body = JSON.stringify({
    items: [{ id: Number(markerVariantId), quantity: 1 }],
    ...(sections.length ? { sections: sections.join(','), sections_url: window.location.pathname } : {}),
  });
  const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
  const json = await response.json();
  if (!response.ok || json.status) throw new Error(json.description || json.message || 'Cart add failed');
  return json.sections ?? undefined;
}

/**
 * Updates the date of the first membership charge from the shopper's clock, because the page
 * that rendered it may have been served from cache.
 * @param {ParentNode} root
 */
function refreshChargeDates(root) {
  for (const time of root.querySelectorAll('time[data-vip-charge-date]')) {
    if (!(time instanceof HTMLTimeElement)) continue;
    const trialDays = Number(time.dataset.trialDays);
    if (!Number.isFinite(trialDays) || trialDays <= 0) continue;

    const date = new Date();
    date.setDate(date.getDate() + trialDays);
    time.dateTime = date.toISOString().slice(0, 10);
    time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || undefined, {
      dateStyle: 'long',
    }).format(date);
  }
}

/**
 * Product page: "Add to cart with VIP membership".
 *
 * @typedef {object} VipBuyRefs
 * @property {HTMLButtonElement} memberButton
 * @property {HTMLElement} status
 *
 * @extends {Component<VipBuyRefs>}
 */
class VipBuyComponent extends Component {
  requiredRefs = ['memberButton', 'status'];

  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    refreshChargeDates(this);
    this.refs.memberButton.disabled = false;
  }

  /**
   * Adds the selected variant and, unless the cart already has it, the membership product in one
   * request, then opens the cart drawer (or goes to the cart page without one). On failure no
   * membership is left behind.
   * @param {MouseEvent} event
   */
  addWithMembership = async (event) => {
    event.preventDefault();
    if (this.#busy) return;

    const form = document.getElementById(this.dataset.formId ?? '');
    const markerVariantId = this.dataset.markerVariantId ?? '';
    if (!(form instanceof HTMLFormElement) || !markerVariantId) return;
    if (!form.reportValidity()) return;

    const variantId = new FormData(form).get('id');
    if (!variantId) return;

    this.#busy = true;
    this.refs.memberButton.setAttribute('aria-busy', 'true');
    this.refs.status.textContent = '';

    let hadMarker = false;
    try {
      hadMarker = markerQuantity(await fetchCart(), markerVariantId) > 0;

      const items = [{ id: Number(variantId), quantity: 1 }];
      if (!hadMarker) items.push({ id: Number(markerVariantId), quantity: 1 });

      if (hasCartDrawer()) {
        await addLines(this, items);
        this.#busy = false;
        this.refs.memberButton.removeAttribute('aria-busy');
        return;
      }

      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }));
      const json = await response.json();
      if (!response.ok || json.status) throw new Error(json.description || json.message || 'Add to cart failed');

      window.location.assign(this.dataset.cartUrl || Theme.routes.cart_url);
    } catch (error) {
      console.warn('[vip-membership] Member add to cart failed:', error);
      this.refs.status.textContent = this.dataset.error ?? '';
      if (!hadMarker) await this.#removeStrayMarker(markerVariantId);
      this.#busy = false;
      this.refs.memberButton.removeAttribute('aria-busy');
    }
  };

  /** @param {string} markerVariantId */
  async #removeStrayMarker(markerVariantId) {
    try {
      if (markerQuantity(await fetchCart(), markerVariantId) > 0) await setMarkerQuantity(markerVariantId, 0);
    } catch (error) {
      console.warn('[vip-membership] Could not remove membership after a failed add:', error);
    }
  }
}

/**
 * Cart: "Join VIP membership" checkbox. Ticking joins, unticking leaves, and the cart re-renders
 * through the theme's own cart update event so prices, totals and the cart count stay in step.
 *
 * @typedef {object} VipToggleRefs
 * @property {HTMLInputElement} checkbox
 * @property {HTMLElement} status
 *
 * @extends {Component<VipToggleRefs>}
 */
class VipMembershipToggleComponent extends Component {
  requiredRefs = ['checkbox', 'status'];

  connectedCallback() {
    super.connectedCallback();
    refreshChargeDates(this);
    this.#heal();
  }

  updatedCallback() {
    super.updatedCallback();
    refreshChargeDates(this);
    this.#heal();
  }

  onToggle = () => {
    this.#setMembership(this.refs.checkbox.checked ? 1 : 0, true);
  };

  /** Keeps the cart at exactly one membership line, and none once no products are left. */
  #heal() {
    const quantity = Number(this.dataset.markerQuantity);
    const productLines = Number(this.dataset.productLines);
    if (quantity > 0 && productLines === 0) this.#setMembership(0);
    else if (quantity > 1) this.#setMembership(1);
  }

  #pending = false;

  /**
   * @param {number} quantity - 1 to join, 0 to leave.
   * @param {boolean} [fromCheckbox] - Whether the customer changed the checkbox, so a failure restores it.
   */
  async #setMembership(quantity, fromCheckbox = false) {
    const markerVariantId = this.dataset.markerVariantId ?? '';
    if (this.#pending || !markerVariantId) return;
    this.#pending = true;
    this.refs.checkbox.disabled = true;
    this.refs.status.textContent = '';

    const sections = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sections.push(element.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent(
        quantity > 0
          ? {
              action: 'add',
              context: 'cart',
              lines: [{ merchandiseId: markerVariantId, quantity: 1 }],
              promise: deferred.promise,
            }
          : {
              action: 'remove',
              context: 'cart',
              lines: [{ id: markerVariantId, quantity: 0 }],
              promise: deferred.promise,
            }
      )
    );

    try {
      const cart = await fetchCart();
      const current = markerQuantity(cart, markerVariantId);
      let json = { ...cart, sections: undefined };
      if (current === 0 && quantity > 0) {
        // /cart/change.js only edits existing lines, so joining adds the membership line.
        const sectionsHtml = await addMarker(markerVariantId, sections);
        json = { ...(await fetchCart()), sections: sectionsHtml };
      } else if (current !== quantity) {
        json = await setMarkerQuantity(markerVariantId, quantity, sections);
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(json),
        detail: {
          sections: json.sections ?? undefined,
          items: json.items,
          itemCount: json.item_count,
          source: 'vip-membership-toggle',
          didError: false,
        },
      });
    } catch (error) {
      console.warn('[vip-membership] Membership update failed:', error);
      if (fromCheckbox) this.refs.checkbox.checked = !this.refs.checkbox.checked;
      this.refs.status.textContent = this.dataset.error ?? '';
      deferred.reject(error);
    } finally {
      this.#pending = false;
      if (this.isConnected) this.refs.checkbox.disabled = false;
    }
  }
}

if (!customElements.get('vip-buy-component')) customElements.define('vip-buy-component', VipBuyComponent);
if (!customElements.get('vip-membership-toggle-component')) {
  customElements.define('vip-membership-toggle-component', VipMembershipToggleComponent);
}
